import mixpanel from 'mixpanel-browser';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { UserTrackingEvents } from '@revelio/core';

/** `page_type` for an app route, as opposed to a CustomDash page's `PageType`. */
export const APP_PAGE_TYPE = 'APP';

interface UsePageVisitTrackingOptions {
  page: string;
  /**
   * Reported as `page_id`, which the Mixpanel "Page entity" custom property
   * maps. App routes derive it from the route path (`derivePageId`);
   * CustomDash pages send their RevelioPageId or the custom page's uuid.
   * Required: a visit without it can't be attributed to a page.
   */
  pageId: string;
  /** `APP_PAGE_TYPE`, or a CustomDash page's `PageType`. */
  pageType: string;
  /**
   * Reported as `path`, and what a re-fire keys on. Defaults to the current
   * pathname, so a page whose pathname changes under it counts each as a
   * visit. Routes pass their own path pattern so stepping through nested
   * routes stays one visit of the page they belong to.
   */
  path?: string;
  /**
   * Extra properties sent with the visit only (not the leave), read when the
   * visit fires — a change on its own doesn't re-fire it.
   */
  visitProperties?: Record<string, unknown>;
}

export const usePageVisitTracking = ({
  page,
  pageId,
  pageType,
  path: pathOverride,
  visitProperties,
}: UsePageVisitTrackingOptions) => {
  const location = useLocation();
  const path = pathOverride ?? location.pathname;
  const visitPropertiesRef = useRef(visitProperties);
  visitPropertiesRef.current = visitProperties;

  useEffect(() => {
    const properties = {
      page,
      path,
      page_id: pageId,
      page_type: pageType,
    };
    mixpanel.track(UserTrackingEvents.PAGE_VISIT, {
      ...visitPropertiesRef.current,
      ...properties,
    });
    mixpanel.time_event(UserTrackingEvents.PAGE_LEAVE);

    return () => {
      mixpanel.track(UserTrackingEvents.PAGE_LEAVE, properties);
    };
  }, [page, path, pageId, pageType]);
};
